import React from "react";
import { cn } from "../../utils/cn";

const Input = React.forwardRef(({
    className,
    type = "text",
    label,
    description,
    error,
    required = false,
    id,
    ...props
}, ref) => {
    const inputId = id || `input-${Math.random()?.toString(36)?.substr(2, 9)}`;

    const baseInputClasses = "flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200";

    if (type === "checkbox") {
        return (
            <input
                type="checkbox"
                className={cn("h-3.5 w-3.5 border-input cursor-pointer", className)}
                ref={ref}
                id={inputId}
                {...props}
            />
        );
    }

    if (type === "radio") {
        return (
            <input
                type="radio"
                className={cn("h-3.5 w-3.5 rounded-full border-input cursor-pointer", className)}
                ref={ref}
                id={inputId}
                {...props}
            />
        );
    }

    // Wire help text and errors to the field so assistive tech reads them with
    // it. Without aria-describedby, a screen-reader user hears the label and
    // then a bare input — the reason it was rejected is on screen but never
    // announced.
    const describedBy = [
        description && !error ? `${inputId}-description` : null,
        error ? `${inputId}-error` : null,
    ].filter(Boolean).join(' ') || undefined;

    return (
        <div className="space-y-1.5">
            {label && (
                <label
                    htmlFor={inputId}
                    className={cn(
                        "text-xs font-medium",
                        error ? "text-error" : "text-foreground"
                    )}
                >
                    {label}
                    {required && (
                        <>
                            {/* The asterisk is decoration; the meaning is carried
                                by aria-required plus this text for screen readers. */}
                            <span aria-hidden="true" className="text-error ltr:ml-0.5 rtl:mr-0.5">*</span>
                            <span className="sr-only"> (required)</span>
                        </>
                    )}
                </label>
            )}

            <input
                type={type}
                className={cn(
                    baseInputClasses,
                    error && "border-error focus:ring-error/20 focus:border-error",
                    className
                )}
                ref={ref}
                id={inputId}
                aria-invalid={error ? true : undefined}
                aria-required={required || undefined}
                aria-describedby={describedBy}
                {...props}
            />

            {description && !error && (
                <p id={`${inputId}-description`} className="text-xs text-muted-foreground">
                    {description}
                </p>
            )}

            {error && (
                // role="alert" so the message is announced the moment it appears,
                // rather than sitting silently next to a field the user has
                // already moved on from.
                <p id={`${inputId}-error`} role="alert" className="text-xs text-error">
                    {error}
                </p>
            )}
        </div>
    );
});

Input.displayName = "Input";

export default Input;
